import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { simplifyPath } from '@src/features/zones/utils';
import { ZoneType } from "@src/core/features/zones/entites/zone-type";
import { selectFreehandShapeUponCreation, setFreehandShapeUponCreation } from '@src/features/zones/redux';
import { useDispatch, useSelector } from "react-redux";
import { ZoneTypeMarkerColor } from '@src/features/zones/config/zones-constants';

interface FreehandShapeDrawerProps {
  map: google.maps.Map | null;
  active?: boolean;
  onComplete?: (path: google.maps.LatLngLiteral[]) => void;
}

const coordsKey = (coords: google.maps.LatLngLiteral[] | undefined) =>
  JSON.stringify(
    (coords ?? []).map((c) => ({ lat: Math.round(c.lat * 1e6) / 1e6, lng: Math.round(c.lng * 1e6) / 1e6 }))
  );

const mvcPathToLiterals = (p: google.maps.MVCArray<google.maps.LatLng>) => {
  const out: google.maps.LatLngLiteral[] = [];
  for (let i = 0; i < p.getLength(); i += 1) {
    out.push(p.getAt(i)!.toJSON());
  }
  return out;
};

const DISC_PX = 20; 
const EDGE_DISC_PX = 10; 
const EDGE_STROKE = 1.15; 
const INSET_R = 0.8;

const vertexHandleDataUrl = (fill: string, n: number) => {
  const fs = n >= 10 ? 8 : 9;
  const c = DISC_PX / 2;
  const r = c * INSET_R;
  const s = DISC_PX;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">`
    + `<circle cx="${c}" cy="${c}" r="${r}" fill="${fill}"/>`
    + `<text x="${c}" y="${c}" text-anchor="middle" dominant-baseline="central" fill="#ffffff" font-size="${fs}" font-weight="600" font-family="system-ui,Segoe UI,Roboto,sans-serif">${n}</text>`
    + `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/** Mid-edge point: 50% white fill inside, zone-colored ring on top. */
const edgeInsertHandleDataUrl = (zoneStroke: string) => {
  const c = EDGE_DISC_PX / 2;
  const s = EDGE_DISC_PX;
  const w = EDGE_STROKE;
  const r = c - 0.5 * w - 0.3; 
  const rFill = Math.max(0, r - 0.5 * w);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}">`
    + `<circle cx="${c}" cy="${c}" r="${rFill}" fill="#ffffff" fill-opacity="0.5" stroke="none"/>`
    + `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="${zoneStroke}" stroke-width="${w}"/>`
    + `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const insertPathIndex = (n: number, edgeIndex: number, isClosed: boolean) => {
  if (n < 2) return 0;
  if (isClosed) {
    if (edgeIndex < n - 1) return edgeIndex + 1;
    return n;
  }
  return edgeIndex + 1;
};

/** Half the gap in meters, centered on the edge — stroke does not run through the hollow handle. */
const EDGE_GAP_HALF_M = 4;

/** Two geodesic segments (or one if edge too short), each a 2-point path for Polyline. */
const gappedEdgeSegments = (
  a: google.maps.LatLngLiteral,
  b: google.maps.LatLngLiteral
): [google.maps.LatLngLiteral, google.maps.LatLngLiteral][] => {
  const aLL = new google.maps.LatLng(a.lat, a.lng);
  const bLL = new google.maps.LatLng(b.lat, b.lng);
  const s = window.google?.maps?.geometry?.spherical;
  if (!s) {
    return [[a, b]];
  }
  const L = s.computeDistanceBetween(aLL, bLL);
  if (L < 0.1) {
    return [[a, b]];
  }
  const f = EDGE_GAP_HALF_M / L;
  if (0.5 - f <= 0.02) {
    return [[a, b]];
  }
  const t1 = Math.max(0.01, 0.5 - f);
  const t2 = Math.min(0.99, 0.5 + f);
  const p1 = s.interpolate(aLL, bLL, t1);
  const p2 = s.interpolate(aLL, bLL, t2);
  return [
    [a, p1.toJSON() as google.maps.LatLngLiteral],
    [p2.toJSON() as google.maps.LatLngLiteral, b],
  ];
};

const FreehandShapeDrawer: React.FC<FreehandShapeDrawerProps> = ({ map, active, onComplete }) => {
  const dispatch = useDispatch();
  const freehandShape = useSelector(selectFreehandShapeUponCreation);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const freehandRef = useRef(freehandShape);
  freehandRef.current = freehandShape;
  const lastDispatchedSimplifiedKey = useRef<string>('');

  const [path, setPath] = useState<google.maps.LatLngLiteral[]>([]);
  const pathRef = useRef(path);
  pathRef.current = path;
  // Content key for when Redux polygon updates from the form (avoids re-running on array ref churn)
  const storePolygonKey = coordsKey(freehandShape?.polygonCoordinates);
  const pathCoordsKey = useMemo(() => coordsKey(path), [path]);

  const stroke = useMemo(
    () => ZoneTypeMarkerColor[freehandShape?.type || ZoneType.TEMPORARY],
    [freehandShape?.type]
  );

  const overlayRef = useRef<{
    handleMarkers: google.maps.Marker[];
    edgeStrokes: google.maps.Polyline[];
    polyline: google.maps.Polyline | null;
    polygon: google.maps.Polygon | null;
    mapClick: google.maps.MapsEventListener | null;
  }>({ handleMarkers: [], edgeStrokes: [], polyline: null, polygon: null, mapClick: null });

  const clearAllOverlays = useCallback((m: google.maps.Map) => {
    const o = overlayRef.current;
    if (o.mapClick) {
      google.maps.event.removeListener(o.mapClick);
      o.mapClick = null;
    }
    o.handleMarkers.forEach((h) => h.setMap(null));
    o.handleMarkers = [];
    o.edgeStrokes.forEach((p) => p.setMap(null));
    o.edgeStrokes = [];
    o.polyline?.setMap(null);
    o.polygon?.setMap(null);
    o.polyline = null;
    o.polygon = null;
    m.setOptions({ draggableCursor: "default" });
  }, []);

  const lastStorePolygonCount = useRef<number | null>(null);

  // Clear local path when turning off; reset ref so a new visit to stage-2 is not mis-detected
  useEffect(() => {
    if (!active) {
      setPath([]);
      lastStorePolygonCount.current = null;
    }
  }, [active]);

  // Clear drawing when user hits Clear in sidebar after a completed polygon (store was non-empty, now empty)
  useEffect(() => {
    if (!active) return;
    const n = freehandShape?.polygonCoordinates?.length ?? 0;
    if (
      lastStorePolygonCount.current != null
      && lastStorePolygonCount.current > 0
      && n === 0
      && path.length > 0
    ) {
      setPath([]);
    }
    lastStorePolygonCount.current = n;
  }, [active, freehandShape?.polygonCoordinates, path.length]);

  // First load: hydrate from redux (e.g. edit) while local path is still empty
  useEffect(() => {
    if (!active) return;
    if (path.length > 0) return;
    const fromStore = freehandShape?.polygonCoordinates;
    if (fromStore && fromStore.length > 0) {
      setPath(fromStore);
    }
  }, [active, freehandShape?.polygonCoordinates, freehandShape?.id, path.length]);

  // When Redux polygon changes from the stage-2 form (or elsewhere), keep local path + map in sync.
  useEffect(() => {
    if (!active) return;
    const fromStore = freehandRef.current?.polygonCoordinates;
    if (!fromStore?.length) return;
    setPath((current) =>
      (coordsKey(current) === coordsKey(fromStore) ? current : fromStore.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })))
    );
  }, [active, storePolygonKey]);

  // Persist simplified coordinates for stage-2 form; local path keeps exact geometry.
  // Do not put `freehandShape` in the dependency list: when path>=3, dispatching updates
  // the store and re-triggers the effect → repeated dispatch and a frozen (black) map.
  // Read latest id/name/type from freehandRef instead. Skip duplicate simplification writes.
  useEffect(() => {
    if (!active) return;

    const fh = freehandRef.current;
    const inStore = fh?.polygonCoordinates?.length ?? 0;
    if (path.length === 0 && inStore >= 3) {
      // Edit: zone already in store; do not clobber before local path hydrates
      return;
    }

    const id = fh?.id ?? 0;
    const name = fh?.name ?? "";
    const type = fh?.type ?? ZoneType.TEMPORARY;

    if (path.length >= 3) {
      const simplified = simplifyPath(path);
      const key = coordsKey(simplified);
      if (key === lastDispatchedSimplifiedKey.current) {
        return;
      }
      lastDispatchedSimplifiedKey.current = key;
      dispatch(
        setFreehandShapeUponCreation({
          id,
          name,
          type,
          polygonCoordinates: simplified,
        })
      );
      setPath(simplified);
      onCompleteRef.current?.(simplified);
      return;
    }

    lastDispatchedSimplifiedKey.current = '';

    if (inStore > 0) {
      dispatch(
        setFreehandShapeUponCreation({
          id,
          name,
          type,
          polygonCoordinates: [],
        })
      );
      return;
    }

    if (!fh) {
      dispatch(
        setFreehandShapeUponCreation({
          id: 0,
          name: '',
          type: ZoneType.TEMPORARY,
          polygonCoordinates: [],
        })
      );
    }
  }, [path, active, dispatch]);

  useEffect(() => {
    if (!active) {
      lastDispatchedSimplifiedKey.current = '';
    }
  }, [active]);

  // Recreate the poly only when the drawing mode or vertex count / stroke changes — not on every
  // lat/lng update (that was causing desync with custom handles).
  const pathBuildToken = useMemo((): "none" | "p1" | "line" | "poly" => {
    if (path.length === 0) return "none";
    if (path.length === 1) return "p1";
    if (path.length === 2) return "line";
    return "poly";
  }, [path.length]);

  // Keep the map MVC ring in sync with React `path` (simplify, form, drag). Do not return early when
  // getLength() !== path.length — that left the fill stuck after simplify or N-point changes.
  const pushPathToMvc = (mvc: google.maps.MVCArray<google.maps.LatLng>, fromPath: google.maps.LatLngLiteral[]) => {
    mvc.clear();
    fromPath.forEach((pt) => mvc.push(new google.maps.LatLng(pt.lat, pt.lng)));
  };

  const syncMvcToPathIfNeeded = (mvc: google.maps.MVCArray<google.maps.LatLng>, fromPath: google.maps.LatLngLiteral[]) => {
    if (mvc.getLength() === fromPath.length
      && coordsKey(mvcPathToLiterals(mvc)) === coordsKey(fromPath)
    ) {
      return;
    }
    pushPathToMvc(mvc, fromPath);
  };

  useLayoutEffect(() => {
    if (!map || !active) return;
    if (path.length < 2) return;
    const o = overlayRef.current;
    const fromPath = pathRef.current;
    if (fromPath.length < 2) return;

    if (path.length === 2) {
      const pl = o.polyline;
      if (!pl) return;
      syncMvcToPathIfNeeded(pl.getPath()!, fromPath);
      return;
    }
    const poly = o.polygon;
    if (!poly) return;
    syncMvcToPathIfNeeded(poly.getPath()!, fromPath);
  }, [map, active, path, pathBuildToken]);

  // Polyline / polygon with editable: false — all editing goes through our markers (vertex + edge insert); no default white handles.
  useEffect(() => {
    if (!map || !active) {
      if (map) {
        clearAllOverlays(map);
      }
      return;
    }

    const pathPoints = pathRef.current;
    const fillStroke = stroke && stroke.length > 0 ? stroke : "#999999";
    const o = overlayRef.current;
    if (o.mapClick) {
      google.maps.event.removeListener(o.mapClick);
      o.mapClick = null;
    }
    o.polyline?.setMap(null);
    o.polygon?.setMap(null);
    o.polyline = null;
    o.polygon = null;

    map.setOptions({ draggableCursor: "default" });

    const addVertexFromMapClick: (e: google.maps.MapMouseEvent) => void = (e) => {
      if (!e.latLng) return;
      const p = e.latLng.toJSON();
      setPath((current) => [...current, p]);
    };

    if (pathBuildToken === "none") {
      o.mapClick = map.addListener("click", addVertexFromMapClick);
      return () => {
        if (o.mapClick) {
          google.maps.event.removeListener(o.mapClick);
          o.mapClick = null;
        }
      };
    }

    if (pathBuildToken === "p1") {
      o.mapClick = map.addListener("click", addVertexFromMapClick);
      return () => {
        if (o.mapClick) {
          google.maps.event.removeListener(o.mapClick);
          o.mapClick = null;
        }
      };
    }

    if (pathBuildToken === "line" && pathPoints[0] && pathPoints[1]) {
      o.polyline = new google.maps.Polyline({
        map,
        path: [pathPoints[0], pathPoints[1]],
        geodesic: true,
        strokeColor: fillStroke,
        strokeOpacity: 0,
        strokeWeight: 2,
        editable: false,
        clickable: true,
        zIndex: 1,
      });
      o.mapClick = map.addListener("click", addVertexFromMapClick);
      return () => {
        o.polyline?.setMap(null);
        o.polyline = null;
        if (o.mapClick) {
          google.maps.event.removeListener(o.mapClick);
          o.mapClick = null;
        }
      };
    }

    o.polygon = new google.maps.Polygon({
      map,
      paths: [...pathPoints],
      strokeColor: fillStroke,
      strokeOpacity: 0,
      strokeWeight: 2,
      fillColor: fillStroke,
      fillOpacity: 0.25,
      editable: false,
      clickable: true,
      zIndex: 1,
    });
    o.mapClick = map.addListener("click", addVertexFromMapClick);
    return () => {
      o.polygon?.setMap(null);
      o.polygon = null;
      if (o.mapClick) {
        google.maps.event.removeListener(o.mapClick);
        o.mapClick = null;
      }
    };
  }, [map, active, pathBuildToken, stroke, clearAllOverlays]);

  // Visible border = short geodesic segments; gap at edge midpoints so stroke does not cross the hollow handle.
  useEffect(() => {
    const o = overlayRef.current;
    o.edgeStrokes.forEach((p) => p.setMap(null));
    o.edgeStrokes = [];
    if (!map || !active) return;
    if (pathBuildToken !== "line" && pathBuildToken !== "poly") return;
    const pts = pathRef.current;
    if (pts.length < 2) return;
    const isClosed = pathBuildToken === "poly" && pts.length >= 3;
    const n = pts.length;
    if (!isClosed && n !== 2) return;
    const fillVisual = (stroke && stroke.length > 0 ? stroke : "#999999");
    const edgeCount = isClosed ? n : n - 1;
    for (let e = 0; e < edgeCount; e += 1) {
      const a = isClosed ? pts[e]! : pts[e]!;
      const b = isClosed ? pts[(e + 1) % n]! : pts[e + 1]!;
      const segs = gappedEdgeSegments(a, b);
      for (const [p0, p1] of segs) {
        o.edgeStrokes.push(
          new google.maps.Polyline({
            map,
            path: [p0, p1],
            geodesic: true,
            strokeColor: fillVisual,
            strokeOpacity: 1,
            strokeWeight: 2,
            clickable: false,
            zIndex: 2,
          })
        );
      }
    }
    return () => {
      o.edgeStrokes.forEach((p) => p.setMap(null));
      o.edgeStrokes = [];
    };
  }, [map, active, pathCoordsKey, pathBuildToken, stroke]);

  // Draggable vertex markers = zone-colored disc + white number. Edge "plus" = insert (route insert_at), drag to place.
  useEffect(() => {
    const o = overlayRef.current;
    if (!map || !active) {
      o.handleMarkers.forEach((h) => h.setMap(null));
      o.handleMarkers = [];
      return;
    }
    const pts = pathRef.current;
    if (pts.length < 1) {
      o.handleMarkers.forEach((h) => h.setMap(null));
      o.handleMarkers = [];
      return;
    }

    o.handleMarkers.forEach((h) => h.setMap(null));
    o.handleMarkers = [];

    const fill = stroke && stroke.length > 0 ? stroke : "#999999";
    const mvc = o.polyline?.getPath() ?? o.polygon?.getPath() ?? null;
    const isLine = pathBuildToken === "line" && pts.length === 2;
    const isPoly = pathBuildToken === "poly" && pts.length >= 3;
    const isClosed = isPoly;
    const n = pts.length;
    const hDisc = DISC_PX / 2;
    const hEdge = EDGE_DISC_PX / 2;

    const canDragVert = mvc && (isLine || isPoly);

    pts.forEach((pt, i) => {
      const m = new google.maps.Marker({
        position: pt,
        map,
        draggable: !!canDragVert,
        zIndex: 1_200_000 + i,
        optimized: false,
        crossOnDrag: false,
        icon: {
          url: vertexHandleDataUrl(fill, i + 1),
          scaledSize: new google.maps.Size(DISC_PX, DISC_PX),
          anchor: new google.maps.Point(hDisc, hDisc),
        },
      });
      o.handleMarkers.push(m);

      if (canDragVert) {
        m.addListener("drag", () => {
          const p = m.getPosition()!;
          mvc.setAt(i, p);
        });
        m.addListener("dragend", () => {
          const ring = o.polyline?.getPath() ?? o.polygon?.getPath();
          if (ring) setPath(mvcPathToLiterals(ring));
        });
      }
    });

    if (mvc && (isLine || isPoly)) {
      const edgeCount = isClosed ? n : n - 1;
      for (let e = 0; e < edgeCount; e += 1) {
        const a = isClosed ? pts[e]! : pts[e]!;
        const b = isClosed ? pts[(e + 1) % n]! : pts[e + 1]!;
        const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
        const m = new google.maps.Marker({
          position: mid,
          map,
          draggable: true,
          zIndex: 1_000_000 + e,
          optimized: false,
          crossOnDrag: false,
          icon: {
            url: edgeInsertHandleDataUrl(fill),
            scaledSize: new google.maps.Size(EDGE_DISC_PX, EDGE_DISC_PX),
            anchor: new google.maps.Point(hEdge, hEdge),
          },
        });
        o.handleMarkers.push(m);
        m.addListener("dragend", () => {
          const ring = o.polyline?.getPath() ?? o.polygon?.getPath();
          if (!ring) return;
          const nNow = ring.getLength();
          if (e >= nNow) return;
          const at = insertPathIndex(nNow, e, isClosed);
          const p = m.getPosition()!;
          const ll = new google.maps.LatLng(p.lat(), p.lng());
          if (at <= ring.getLength()) {
            ring.insertAt(at, ll);
            setPath(mvcPathToLiterals(ring));
          }
        });
      }
    }

    return () => {
      o.handleMarkers.forEach((h) => h.setMap(null));
      o.handleMarkers = [];
    };
  }, [map, active, pathCoordsKey, pathBuildToken, stroke]);

  return null;
};

export default FreehandShapeDrawer;
